import { useState, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "../api/config";
import { getReviewDetail } from "../api/requests/review.requests";

interface UseSignedImageOptions {
  imageId: number;
  initialUrl: string;
}

interface UseSignedImageResult {
  src: string;
  onError: () => void;
  isRefreshing: boolean;
  hasFailed: boolean;
}

export const useSignedImage = ({
  imageId,
  initialUrl,
}: UseSignedImageOptions): UseSignedImageResult => {
  const queryClient = useQueryClient();
  const [src, setSrc] = useState(initialUrl);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const retriedRef = useRef(false);

  const prevImageIdRef = useRef(imageId);
  if (prevImageIdRef.current !== imageId) {
    prevImageIdRef.current = imageId;
    setSrc(initialUrl);
    setIsRefreshing(false);
    setHasFailed(false);
    retriedRef.current = false;
  }

  const prevUrlRef = useRef(initialUrl);
  if (prevUrlRef.current !== initialUrl) {
    prevUrlRef.current = initialUrl;
    setSrc(initialUrl);
    retriedRef.current = false;
    setHasFailed(false);
  }

  const onError = useCallback(async () => {
    if (retriedRef.current) {
      setHasFailed(true);
      return;
    }
    retriedRef.current = true;
    setIsRefreshing(true);
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: [QUERY_KEYS.REVIEW_DETAIL, imageId],
        queryFn: () => getReviewDetail(imageId),
        staleTime: 0,
      });
      if (fresh?.signed_url) {
        setSrc(fresh.signed_url);
      } else {
        setHasFailed(true);
      }
    } catch {
      setHasFailed(true);
    } finally {
      setIsRefreshing(false);
    }
  }, [imageId, queryClient]);

  return { src, onError, isRefreshing, hasFailed };
};
